// src/Pages/Subscription/MySubscription.tsx
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import axios from "axios";
import "./MySubscription.scss";
import { subscriptionAPI } from "../../utils/api/subscriptionAPI";
import { BASE_API_URL } from "../../utils/consts";

// Types
interface SubscriptionStatus {
  hasSubscription: boolean;
  canAccess: boolean;
  isAccessBlocked: boolean;
  blockReason?: string;
  status: "TRIAL" | "ACTIVE" | "PAUSED" | "CANCELLED" | "OVERDUE";
  planName: string;
  monthsSinceStart: number;
  startDate: string; // Format: "YYYY-MM-DD"
}

interface BillingPeriod {
  _id: string;
  periodMonth: string;
  periodStartDate: string;
  periodEndDate: string;
  calculatedPrice: number;
  currency: string;
  status: "PENDING" | "UNPAID" | "PAID" | "OVERDUE" | "WAIVED";
  dueDate: string;
  paidAt?: string;
  daysUntilDue: number;
  daysOverdue: number;
  employeeCount: number;
  billingModel: "FIXED_MONTHLY" | "PER_EMPLOYEE";
  basePrice: number;
  pricePerEmployee: number;
  includedEmployees: number;
  billableEmployees: number;
}

interface CurrentPeriod {
  month: string;
  price: number;
  employeeCount: number;
  status: string;
  dueDate: string;
  daysUntilDue: number;
  daysOverdue: number;
}

interface EmployeeStats {
  totalUsers: number;
  activeNonFrozen: number;
  frozenUsers: number;
  includedInPlan: number;
  billableAbove: number;
  extraMonthlyCost: number;
}

interface Plan {
  name: string;
  billingModel: string;
  basePrice: number;
  pricePerEmployee: number;
  currency: string;
  pricingPhases?: PricingPhase[];
  includedEmployees?: number;
  description?: string;
}

interface PricingPhase {
  fromMonth: number;
  toMonth: number | null;
  price: number;
  description?: string;
}

interface SubscriptionSummary {
  hasSubscription: boolean;
  subscription: SubscriptionStatus;
  plan: Plan;
  employeeStats: EmployeeStats;
  currentPeriod: CurrentPeriod | null;
  stats: {
    totalMonthsPaid: number;
    totalMonthsUnpaid: number;
    totalMonthsOverdue: number;
    totalRevenue: number;
  };
  recentPeriods: BillingPeriod[];
}

interface VoiceFeatureStat { calls: number; seconds: number; }
interface VoiceUsageSummary {
  monthKey: string;
  totalCalls: number;
  successCalls: number;
  failedCalls: number;
  partialCalls: number;
  totalSeconds: number;
  billableMinutes: number;
  byFeature: {
    assistant: VoiceFeatureStat;
    shift: VoiceFeatureStat;
    public_reservation: VoiceFeatureStat;
  };
}

interface AiUsageMonth {
  month: string;
  count: number;
  costEur: number;
  questionsCount: number;
  questionsCostEur: number;
  totalCostEur: number;
  lastUsedAt: string;
}
interface AiUsageSummary {
  byMonth: AiUsageMonth[];
  totalCount: number;
  totalCostEur: number;
  totalQuestionsCount: number;
  totalQuestionsCostEur: number;
  grandTotalCostEur: number;
}

const MySubscription: React.FC = () => {
  const { t } = useTranslation();
  const userData = useSelector((state: any) => state.userData.user);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<SubscriptionSummary | null>(null);
  const [selectedYear, setSelectedYear] = useState<number | "all">(
    new Date().getFullYear(),
  );
  const [selectedMonth, setSelectedMonth] = useState<number | "all">("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [error, setError] = useState<string | null>(null);

  // Voice usage state
  const currentMonthKey = new Date().toISOString().slice(0, 7); // "YYYY-MM"
  const [voiceMonth, setVoiceMonth] = useState(currentMonthKey);
  const [voiceUsage, setVoiceUsage] = useState<VoiceUsageSummary | null>(null);
  const [voiceLoading, setVoiceLoading] = useState(false);

  // AI analysis usage state
  const [aiUsage, setAiUsage] = useState<AiUsageSummary | null>(null);
  const [aiUsageLoading, setAiUsageLoading] = useState(false);

  // Get available years from subscription start date
  const getAvailableYears = () => {
    if (!summary?.subscription.startDate) return [];
    const startYear = new Date(summary.subscription.startDate).getFullYear();
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = startYear; year <= currentYear; year++) {
      years.push(year);
    }
    return years;
  };

  // Month names for dropdown
  const months = [
    { value: "all", label: t("common.all") || "Svi meseci" },
    { value: 1, label: t("monthNames.january") || "Januar" },
    { value: 2, label: t("monthNames.february") || "Februar" },
    { value: 3, label: t("monthNames.march") || "Mart" },
    { value: 4, label: t("monthNames.april") || "April" },
    { value: 5, label: t("monthNames.may") || "Maj" },
    { value: 6, label: t("monthNames.june") || "Jun" },
    { value: 7, label: t("monthNames.july") || "Jul" },
    { value: 8, label: t("monthNames.august") || "Avgust" },
    { value: 9, label: t("monthNames.september") || "Septembar" },
    { value: 10, label: t("monthNames.october") || "Oktobar" },
    { value: 11, label: t("monthNames.november") || "Novembar" },
    { value: 12, label: t("monthNames.december") || "Decembar" },
  ];

  // Fetch subscription summary
  useEffect(() => {
    fetchSubscriptionSummary();
  }, []);

  // Fetch voice usage
  useEffect(() => {
    if (!userData?.clientId) return;
    setVoiceLoading(true);
    axios
      .get(`${BASE_API_URL}/voice-usage/monthly`, {
        params: { clientId: userData.clientId, month: voiceMonth },
      })
      .then((res) => setVoiceUsage(res.data))
      .catch(() => setVoiceUsage(null))
      .finally(() => setVoiceLoading(false));
  }, [userData?.clientId, voiceMonth]);

  // Fetch AI analysis usage
  useEffect(() => {
    if (!userData?.clientId) return;
    setAiUsageLoading(true);
    axios
      .get(`${BASE_API_URL}/${userData.clientId}/dashboard-ai-usage`, {
        headers: { "x-access-token": userData.accessToken || "" },
      })
      .then((res) => setAiUsage(res.data))
      .catch(() => setAiUsage(null))
      .finally(() => setAiUsageLoading(false));
  }, [userData?.clientId]);

  const fetchSubscriptionSummary = async () => {
    try {
      setLoading(true);
      const response = await subscriptionAPI.getMySubscription();
      console.log("=== SUBSCRIPTION API RESPONSE ===");
      console.log("Full Response:", response.data);
      console.log("Has Subscription:", response.data.data?.hasSubscription);
      console.log("Recent Periods:", response.data.data?.recentPeriods);
      console.log("Current Period:", response.data.data?.currentPeriod);
      console.log(
        "Subscription Start Date:",
        response.data.data?.subscription?.startDate,
      );
      console.log("================================");
      setSummary(response.data.data); // API returns {success: true, data: {...}}
      setError(null);
    } catch (err: any) {
      console.error("Error fetching subscription:", err);
      setError(err.response?.data?.message || "Failed to load subscription");
    } finally {
      setLoading(false);
    }
  };

  // Status badge styling
  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case "PAID":
        return "badge-success";
      case "UNPAID":
        return "badge-warning";
      case "OVERDUE":
        return "badge-danger";
      case "WAIVED":
        return "badge-info";
      case "PENDING":
        return "badge-secondary";
      default:
        return "badge-light";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "PAID":
        return t("subscription.status.paid");
      case "UNPAID":
        return t("subscription.status.unpaid");
      case "OVERDUE":
        return t("subscription.status.overdue");
      case "WAIVED":
        return t("subscription.status.waived");
      case "PENDING":
        return t("subscription.status.pending");
      default:
        return status;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("sr-RS", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat("sr-RS", {
      style: "currency",
      currency: currency || "EUR",
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="my-subscription-page">
        <div className="loading-container">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p>{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  if (error || !summary?.hasSubscription) {
    return (
      <div className="my-subscription-page">
        <div className="no-subscription-container">
          <div className="alert alert-warning">
            <h4>{t("subscription.noSubscription.title")}</h4>
            <p>{error || t("subscription.noSubscription.message")}</p>
            <p>{t("subscription.noSubscription.contactAdmin")}</p>
          </div>
        </div>
      </div>
    );
  }

  const { subscription, plan, employeeStats, currentPeriod, stats, recentPeriods } = summary;

  // Calculate stats only for periods after startDate (same filter as history)
  const getFilteredStats = () => {
    if (!recentPeriods || !subscription.startDate) return stats;

    const startYearMonth = subscription.startDate.substring(0, 7); // "2025-10"
    const filteredPeriods = recentPeriods.filter(
      (period) => period.periodMonth >= startYearMonth,
    );

    return {
      totalMonthsPaid: filteredPeriods.filter((p) => p.status === "PAID")
        .length,
      totalMonthsUnpaid: filteredPeriods.filter((p) => p.status === "UNPAID")
        .length,
      totalMonthsOverdue: filteredPeriods.filter((p) => p.status === "OVERDUE")
        .length,
      totalRevenue: filteredPeriods
        .filter((p) => p.status === "PAID")
        .reduce((sum, p) => sum + p.calculatedPrice, 0),
    };
  };

  const filteredStats = getFilteredStats();

  // Last 12 months for voice month selector
  const voiceMonthOptions = Array.from({ length: 12 }, (_, i) => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - i);
    return d.toISOString().slice(0, 7);
  });

  const fmtSeconds = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
  };

  return (
    <div className="my-subscription-page">
      {/* Header */}
      <div className="page-header">
        <h1>{t("subscription.title")}</h1>
        <p className="text-muted">{t("subscription.subtitle")}</p>
      </div>

      {/* Status Banner */}
      <div
        className={`subscription-banner status-${subscription.status.toLowerCase()}`}
      >
        <div className="banner-content">
          <div className="status-icon">
            {subscription.canAccess ? (
              <i className="bi bi-check-circle-fill"></i>
            ) : (
              <i className="bi bi-exclamation-triangle-fill"></i>
            )}
          </div>
          <div className="status-text">
            <h3>
              {subscription.canAccess
                ? t("subscription.banner.active")
                : t("subscription.banner.inactive")}
            </h3>
            <p>
              {plan.name} •{" "}
              {t("subscription.status." + subscription.status.toLowerCase())}
            </p>
            {subscription.isAccessBlocked && (
              <div className="alert alert-danger mt-2">
                <strong>{t("subscription.accessBlocked")}</strong>
                <p>{subscription.blockReason}</p>
              </div>
            )}
          </div>
          <div className="status-meta">
            <span className="months-active">
              {subscription.monthsSinceStart} {t("subscription.monthsActive")}
            </span>
          </div>
        </div>
      </div>

      {/* Employee Stats Section */}
      {employeeStats && plan.billingModel === "PER_EMPLOYEE" && (
        <div className="employee-stats-section">
          <div className="card">
            <div className="card-header">
              <h4>
                <i className="bi bi-people-fill"></i>{" "}
                {t("subscription.employeeStats.title")}
              </h4>
              <p className="subtitle">
                {t("subscription.employeeStats.subtitle")}
              </p>
            </div>
            <div className="card-body">
              <div className="employee-stats-grid">
                <div className="employee-stat-card active">
                  <div className="stat-icon">
                    <i className="bi bi-person-check-fill"></i>
                  </div>
                  <div className="stat-content">
                    <div className="stat-value">{employeeStats.activeNonFrozen}</div>
                    <div className="stat-label">
                      {t("subscription.employeeStats.activeNonFrozen")}
                    </div>
                  </div>
                </div>

                <div className="employee-stat-card frozen">
                  <div className="stat-icon">
                    <i className="bi bi-person-dash-fill"></i>
                  </div>
                  <div className="stat-content">
                    <div className="stat-value">{employeeStats.frozenUsers}</div>
                    <div className="stat-label">
                      {t("subscription.employeeStats.frozen")}
                    </div>
                  </div>
                </div>

                <div className="employee-stat-card included">
                  <div className="stat-icon">
                    <i className="bi bi-gift-fill"></i>
                  </div>
                  <div className="stat-content">
                    <div className="stat-value">{employeeStats.includedInPlan}</div>
                    <div className="stat-label">
                      {t("subscription.employeeStats.includedInPlan")}
                    </div>
                  </div>
                </div>

                <div className={`employee-stat-card ${employeeStats.billableAbove > 0 ? "billable-above" : "within-limit"}`}>
                  <div className="stat-icon">
                    <i className={`bi ${employeeStats.billableAbove > 0 ? "bi-person-plus-fill" : "bi-check-circle-fill"}`}></i>
                  </div>
                  <div className="stat-content">
                    <div className="stat-value">{employeeStats.billableAbove}</div>
                    <div className="stat-label">
                      {t("subscription.employeeStats.billableAbove")}
                    </div>
                  </div>
                </div>
              </div>

              {employeeStats.billableAbove > 0 && (
                <div className="extra-cost-alert">
                  <i className="bi bi-exclamation-triangle-fill"></i>
                  <span>
                    {t("subscription.employeeStats.extraCostInfo", {
                      count: employeeStats.billableAbove,
                      cost: formatCurrency(employeeStats.extraMonthlyCost, plan.currency),
                    })}
                  </span>
                </div>
              )}

              {employeeStats.billableAbove === 0 && (
                <div className="within-limit-info">
                  <i className="bi bi-check-circle-fill"></i>
                  <span>
                    {t("subscription.employeeStats.withinLimit")}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Billing History - Moved to top */}
      <div className="billing-history-section">
        <div className="card">
          <div className="card-header">
            <div className="header-content">
              <h4>
                <i className="bi bi-calendar3"></i>
                {t("subscription.billingHistory.title")}
              </h4>
              <p className="subtitle">
                {t("subscription.billingHistory.subtitle") ||
                  "Pregled svih perioda naplate"}
              </p>
            </div>
            <div className="filters-container">
              <div className="filter-group">
                <label>
                  <i className="bi bi-calendar-check"></i> Godina
                </label>
                <select
                  className="form-select form-select-sm"
                  value={selectedYear}
                  onChange={(e) => {
                    const value =
                      e.target.value === "all"
                        ? "all"
                        : parseInt(e.target.value);
                    setSelectedYear(value);
                    setSelectedMonth("all"); // Reset month when year changes
                  }}
                >
                  <option value="all">Sve godine</option>
                  {getAvailableYears().map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>
                  <i className="bi bi-calendar-month"></i> Mesec
                </label>
                <select
                  className="form-select form-select-sm"
                  value={selectedMonth}
                  onChange={(e) => {
                    const value =
                      e.target.value === "all"
                        ? "all"
                        : parseInt(e.target.value);
                    setSelectedMonth(value);
                  }}
                  disabled={selectedYear === "all"}
                >
                  {months.map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>
                  <i className="bi bi-funnel"></i> Status
                </label>
                <select
                  className="form-select form-select-sm"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">Svi statusi</option>
                  <option value="PAID">Plaćeno</option>
                  <option value="UNPAID">Neplaćeno</option>
                  <option value="OVERDUE">Prekoračeno</option>
                  <option value="WAIVED">Otpisano</option>
                  <option value="PENDING">Na čekanju</option>
                </select>
              </div>
              {(selectedYear !== new Date().getFullYear() ||
                selectedMonth !== "all" ||
                statusFilter !== "all") && (
                <button
                  className="btn btn-sm btn-outline-secondary reset-btn"
                  onClick={() => {
                    setSelectedYear(new Date().getFullYear());
                    setSelectedMonth("all");
                    setStatusFilter("all");
                  }}
                  title="Resetuj filtere"
                >
                  <i className="bi bi-arrow-counterclockwise"></i>
                </button>
              )}
            </div>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table billing-table">
                <thead>
                  <tr>
                    <th>{t("subscription.billingHistory.month")}</th>
                    <th>{t("subscription.billingHistory.amount")}</th>
                    <th>{t("subscription.billingHistory.employees")}</th>
                    <th>{t("subscription.billingHistory.status")}</th>
                    <th>{t("subscription.billingHistory.dueDate")}</th>
                    <th>{t("subscription.billingHistory.paidAt")}</th>
                  </tr>
                </thead>
                <tbody>
                  {recentPeriods && recentPeriods.length > 0 ? (
                    (() => {
                      const startYearMonth = subscription.startDate.substring(
                        0,
                        7,
                      );
                      console.log("=== BILLING TABLE RENDER ===");
                      console.log(
                        "Subscription Start Date:",
                        subscription.startDate,
                      );
                      console.log("Start Year-Month filter:", startYearMonth);
                      console.log(
                        "Total Recent Periods:",
                        recentPeriods.length,
                      );
                      console.log(
                        "Periods data:",
                        recentPeriods.map((p) => ({
                          month: p.periodMonth,
                          status: p.status,
                          price: p.calculatedPrice,
                          dueDate: p.dueDate,
                        })),
                      );

                      let filteredPeriods = recentPeriods.filter(
                        (period) => period.periodMonth >= startYearMonth,
                      );

                      console.log(
                        "After start date filter:",
                        filteredPeriods.length,
                      );

                      // Year filter
                      if (selectedYear !== "all") {
                        filteredPeriods = filteredPeriods.filter((period) =>
                          period.periodMonth.startsWith(
                            selectedYear.toString(),
                          ),
                        );
                      }

                      // Month filter
                      if (selectedMonth !== "all") {
                        const monthStr = selectedMonth
                          .toString()
                          .padStart(2, "0");
                        filteredPeriods = filteredPeriods.filter((period) =>
                          period.periodMonth.endsWith(`-${monthStr}`),
                        );
                      }

                      // Status filter
                      if (statusFilter !== "all") {
                        filteredPeriods = filteredPeriods.filter(
                          (period) => period.status === statusFilter,
                        );
                      }

                      if (filteredPeriods.length === 0) {
                        return (
                          <tr>
                            <td colSpan={6} className="text-center no-data">
                              <div className="no-data-container">
                                <i className="bi bi-inbox"></i>
                                <p>Nema perioda za prikazivanje</p>
                                <small>
                                  Promenite filtere da vidite druge periode
                                </small>
                              </div>
                            </td>
                          </tr>
                        );
                      }

                      return filteredPeriods.map((period) => (
                        <tr
                          key={period._id}
                          className={`status-${period.status.toLowerCase()}`}
                        >
                          <td>
                            <strong>{period.periodMonth}</strong>
                          </td>
                          <td className="price">
                            {(() => {
                              const aiMonth = aiUsage?.byMonth?.find((m) => m.month === period.periodMonth);
                              const aiTotal = aiMonth?.totalCostEur || 0;
                              const grandTotal = period.calculatedPrice + aiTotal;
                              return (
                                <>
                                  <div className="price-main">
                                    {formatCurrency(grandTotal, period.currency)}
                                  </div>
                                  {period.billingModel === "PER_EMPLOYEE" && (
                                    <div className="price-breakdown">
                                      <span>{formatCurrency(period.basePrice, period.currency)}</span>
                                      {period.billableEmployees > 0 && (
                                        <span className="extra">
                                          {" + "}
                                          {period.billableEmployees}×{formatCurrency(period.pricePerEmployee, period.currency)}
                                          {" = "}
                                          <strong>{formatCurrency(period.calculatedPrice, period.currency)}</strong>
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  {aiMonth && (
                                    <div className="price-breakdown ai-cost-breakdown">
                                      {aiMonth.count > 0 && (
                                        <span className="ai-cost-line">
                                          <i className="bi bi-stars"></i>
                                          {aiMonth.count}× analiza = {aiMonth.costEur}€
                                        </span>
                                      )}
                                      {aiMonth.questionsCount > 0 && (
                                        <span className="ai-cost-line">
                                          <i className="bi bi-chat-dots"></i>
                                          {aiMonth.questionsCount}× pitanje = {aiMonth.questionsCostEur}€
                                        </span>
                                      )}
                                      <span className="ai-cost-total">
                                        AI ukupno: <strong>{aiMonth.totalCostEur}€</strong>
                                      </span>
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </td>
                          <td>
                            {period.billingModel === "PER_EMPLOYEE" ? (
                              <div className="employee-count-cell">
                                <span className="badge badge-light">
                                  {period.employeeCount}{" "}
                                  {t("subscription.billingHistory.employees")}
                                </span>
                                {period.billableEmployees > 0 && (
                                  <span className="badge badge-warning above-limit">
                                    +{period.billableEmployees} {t("subscription.billingHistory.aboveLimit") || "iznad limita"}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td>
                            <span
                              className={`badge ${getStatusBadgeClass(
                                period.status,
                              )}`}
                            >
                              {getStatusText(period.status)}
                            </span>
                          </td>
                          <td>{formatDate(period.dueDate)}</td>
                          <td>
                            {period.paidAt ? (
                              formatDate(period.paidAt)
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                        </tr>
                      ));
                    })()
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center no-data">
                        <div className="no-data-container">
                          <i className="bi bi-inbox"></i>
                          <p>{t("subscription.billingHistory.noPeriods")}</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Voice Usage Section */}
      <div className="voice-usage-section">
        <div className="card">
          <div className="card-header">
            <div className="header-content">
              <h4>
                <i className="bi bi-mic-fill"></i> {t("AI Voice Commands — Usage")}
              </h4>
              <p className="subtitle">{t("Overview of call count and consumed minutes per function")}</p>
            </div>
            <div className="filters-container">
              <div className="filter-group">
                <label><i className="bi bi-calendar-month"></i> {t("Month")}</label>
                <select
                  className="form-select form-select-sm"
                  value={voiceMonth}
                  onChange={(e) => setVoiceMonth(e.target.value)}
                >
                  {voiceMonthOptions.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div className="card-body">
            {voiceLoading ? (
              <div className="text-center py-3">
                <div className="spinner-border spinner-border-sm text-primary" role="status" />
                <span className="ms-2">{t("Loading...")}</span>
              </div>
            ) : voiceUsage && voiceUsage.totalCalls > 0 ? (
              <>
                {/* Summary row */}
                <div className="voice-usage-summary-grid">
                  <div className="voice-stat-card">
                    <div className="voice-stat-icon"><i className="bi bi-telephone-fill"></i></div>
                    <div className="voice-stat-value">{voiceUsage.totalCalls}</div>
                    <div className="voice-stat-label">{t("Total Calls")}</div>
                  </div>
                  <div className="voice-stat-card success">
                    <div className="voice-stat-icon"><i className="bi bi-check-circle-fill"></i></div>
                    <div className="voice-stat-value">{voiceUsage.successCalls}</div>
                    <div className="voice-stat-label">{t("Successful")}</div>
                  </div>
                  <div className="voice-stat-card danger">
                    <div className="voice-stat-icon"><i className="bi bi-x-circle-fill"></i></div>
                    <div className="voice-stat-value">{voiceUsage.failedCalls}</div>
                    <div className="voice-stat-label">{t("Failed")}</div>
                  </div>
                  {(voiceUsage.partialCalls || 0) > 0 && (
                    <div className="voice-stat-card warning">
                      <div className="voice-stat-icon"><i className="bi bi-exclamation-circle-fill"></i></div>
                      <div className="voice-stat-value">{voiceUsage.partialCalls}</div>
                      <div className="voice-stat-label">{t("Partial")}</div>
                    </div>
                  )}
                  <div className="voice-stat-card minutes">
                    <div className="voice-stat-icon"><i className="bi bi-clock-fill"></i></div>
                    <div className="voice-stat-value">{voiceUsage.billableMinutes}</div>
                    <div className="voice-stat-label">{t("Billable Minutes")}</div>
                  </div>
                </div>

                {/* Feature breakdown */}
                <div className="voice-feature-breakdown">
                  <h5 className="breakdown-title">{t("By Function")}</h5>
                  <div className="voice-feature-table">
                    {([
                      { key: "assistant",          labelKey: "Business Assistant",      icon: "bi-robot" },
                      { key: "shift",              labelKey: "Shift Entry (voice)",     icon: "bi-calendar-week" },
                      { key: "public_reservation", labelKey: "Public Reservation (voice)", icon: "bi-calendar-check" },
                    ] as const).map(({ key, labelKey, icon }) => {
                      const f = voiceUsage.byFeature[key];
                      if (!f || f.calls === 0) return null;
                      return (
                        <div key={key} className="voice-feature-row">
                          <div className="feature-name">
                            <i className={`bi ${icon}`}></i> {t(labelKey)}
                          </div>
                          <div className="feature-calls">{f.calls} {t("calls")}</div>
                          <div className="feature-duration">{fmtSeconds(f.seconds)}</div>
                          <div className="feature-minutes">{Math.ceil(f.seconds / 60)} min</div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="voice-usage-note">
                  <i className="bi bi-info-circle"></i>
                  {t("Duration is estimated based on the number of spoken words (≈90 words/min). Billing is per billable minute (rounded up).")}
                </div>
              </>
            ) : (
              <div className="no-data-container text-center py-4">
                <i className="bi bi-mic-mute" style={{ fontSize: 32, color: "#ccc" }}></i>
                <p className="mt-2 text-muted">{t("No voice calls for")} {voiceMonth}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* AI Analysis Usage Section */}
      <div className="voice-usage-section">
        <div className="card">
          <div className="card-header">
            <div className="header-content">
              <h4>
                <i className="bi bi-stars"></i> AI Analiza poslovanja — Potrošnja
              </h4>
              <p className="subtitle">
                Generacija analize: <strong>€0.50</strong> · Follow-up pitanje: <strong>€0.30</strong> · Historija se ne briše.
              </p>
            </div>
          </div>
          <div className="card-body">
            {aiUsageLoading ? (
              <div className="text-center py-3">
                <div className="spinner-border spinner-border-sm text-primary" role="status" />
                <span className="ms-2">Učitavanje...</span>
              </div>
            ) : aiUsage && (aiUsage.totalCount > 0 || aiUsage.totalQuestionsCount > 0) ? (
              <>
                {/* Summary cards */}
                <div className="voice-usage-summary-grid" style={{ marginBottom: 16 }}>
                  <div className="voice-stat-card">
                    <div className="voice-stat-icon"><i className="bi bi-stars"></i></div>
                    <div className="voice-stat-value">{aiUsage.totalCount}</div>
                    <div className="voice-stat-label">Analiza ukupno</div>
                  </div>
                  <div className="voice-stat-card">
                    <div className="voice-stat-icon"><i className="bi bi-chat-dots-fill"></i></div>
                    <div className="voice-stat-value">{aiUsage.totalQuestionsCount}</div>
                    <div className="voice-stat-label">Pitanja ukupno</div>
                  </div>
                  <div className="voice-stat-card minutes">
                    <div className="voice-stat-icon"><i className="bi bi-currency-euro"></i></div>
                    <div className="voice-stat-value">{aiUsage.grandTotalCostEur}€</div>
                    <div className="voice-stat-label">Ukupni AI trošak</div>
                  </div>
                </div>

                {/* Per-month breakdown */}
                <div className="voice-feature-breakdown">
                  <h5 className="breakdown-title">Po mjesecu</h5>
                  <div className="ai-usage-month-table">
                    <div className="ai-usage-month-header">
                      <span>Mjesec</span>
                      <span>Analize</span>
                      <span>Pitanja</span>
                      <span>Trošak</span>
                    </div>
                    {aiUsage.byMonth.map((m) => (
                      <div key={m.month} className="ai-usage-month-row">
                        <div className="ai-um-month">
                          <i className="bi bi-calendar3"></i> {m.month}
                        </div>
                        <div className="ai-um-cell">
                          {m.count > 0
                            ? <><span className="ai-um-count">{m.count}×</span> <span className="ai-um-price">{m.costEur}€</span></>
                            : <span className="ai-um-zero">—</span>}
                        </div>
                        <div className="ai-um-cell">
                          {m.questionsCount > 0
                            ? <><span className="ai-um-count">{m.questionsCount}×</span> <span className="ai-um-price">{m.questionsCostEur}€</span></>
                            : <span className="ai-um-zero">—</span>}
                        </div>
                        <div className="ai-um-total">
                          <strong>{m.totalCostEur}€</strong>
                        </div>
                      </div>
                    ))}
                    {/* Totals row */}
                    <div className="ai-usage-month-row ai-usage-totals-row">
                      <div className="ai-um-month"><strong>UKUPNO</strong></div>
                      <div className="ai-um-cell">
                        <span className="ai-um-count">{aiUsage.totalCount}×</span>
                        <span className="ai-um-price">{aiUsage.totalCostEur}€</span>
                      </div>
                      <div className="ai-um-cell">
                        <span className="ai-um-count">{aiUsage.totalQuestionsCount}×</span>
                        <span className="ai-um-price">{aiUsage.totalQuestionsCostEur}€</span>
                      </div>
                      <div className="ai-um-total">
                        <strong style={{ fontSize: 15 }}>{aiUsage.grandTotalCostEur}€</strong>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="voice-usage-note">
                  <i className="bi bi-info-circle"></i>
                  Svako pokretanje analize i svako pitanje naplaćuje se posebno. Cijena se prikazuje na billing periodu.
                </div>
              </>
            ) : (
              <div className="no-data-container text-center py-4">
                <i className="bi bi-stars" style={{ fontSize: 32, color: "#ccc" }}></i>
                <p className="mt-2 text-muted">Još niste koristili AI analizu poslovanja.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Current Period Card */}
      {currentPeriod && (
        <div className="current-period-card">
          <div className="card">
            <div className="card-header">
              <h4>{t("subscription.currentPeriod.title")}</h4>
              <span
                className={`badge ${getStatusBadgeClass(currentPeriod.status)}`}
              >
                {getStatusText(currentPeriod.status)}
              </span>
            </div>
            <div className="card-body">
              <div className="period-details">
                <div className="detail-item">
                  <label>{t("subscription.currentPeriod.month")}</label>
                  <strong>{currentPeriod.month}</strong>
                </div>
                <div className="detail-item">
                  <label>{t("subscription.currentPeriod.amount")}</label>
                  <strong className="price">
                    {formatCurrency(currentPeriod.price, plan.currency)}
                  </strong>
                </div>
                <div className="detail-item">
                  <label>{t("subscription.currentPeriod.dueDate")}</label>
                  <strong>{formatDate(currentPeriod.dueDate)}</strong>
                </div>
                {currentPeriod.status === "UNPAID" &&
                  currentPeriod.daysUntilDue > 0 && (
                    <div className="detail-item">
                      <label>
                        {t("subscription.currentPeriod.daysUntilDue")}
                      </label>
                      <span className="badge badge-warning">
                        {currentPeriod.daysUntilDue} {t("common.days")}
                      </span>
                    </div>
                  )}
                {currentPeriod.status === "OVERDUE" && (
                  <div className="detail-item">
                    <label>{t("subscription.currentPeriod.daysOverdue")}</label>
                    <span className="badge badge-danger">
                      {currentPeriod.daysOverdue} {t("days")}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Plan Details */}
      <div className="plan-details-section">
        <div className="card">
          <div className="card-header">
            <h4>
              <i className="bi bi-box-seam"></i>{" "}
              {t("subscription.planDetails.title")}
            </h4>
          </div>
          <div className="card-body">
            {/* Plan Name & Model */}
            <div className="plan-header-info">
              <div className="plan-name-badge">
                <h3>{plan.name}</h3>
                <span className="badge badge-primary">
                  {plan.billingModel === "FIXED_MONTHLY"
                    ? t("subscription.planDetails.fixedMonthly")
                    : t("subscription.planDetails.perEmployee")}
                </span>
              </div>
              {plan.description && (
                <p className="plan-description">{plan.description}</p>
              )}
            </div>

            {/* Pricing Phases - Time-based pricing */}
            {plan.pricingPhases && plan.pricingPhases.length > 0 && (
              <div className="pricing-phases-container">
                <h5 className="section-title">
                  <i className="bi bi-graph-up"></i>{" "}
                  {t("subscription.planDetails.pricingSchedule")}
                </h5>
                <div className="pricing-timeline">
                  {plan.pricingPhases.map((phase, index) => (
                    <div
                      key={index}
                      className={`pricing-phase-card ${
                        phase.price === 0 ? "free-phase" : ""
                      }`}
                    >
                      <div className="phase-badge">
                        {phase.price === 0 ? (
                          <i className="bi bi-gift-fill"></i>
                        ) : (
                          <i className="bi bi-tag-fill"></i>
                        )}
                      </div>
                      <div className="phase-content">
                        <div className="phase-period-label">
                          {phase.toMonth
                            ? t("subscription.planDetails.monthsRange", {
                                from: phase.fromMonth + 1,
                                to: phase.toMonth,
                              })
                            : t("subscription.planDetails.monthsFrom", {
                                from: phase.fromMonth + 1,
                              })}
                        </div>
                        <div className="phase-price-display">
                          {phase.price === 0 ? (
                            <span className="free-label">FREE</span>
                          ) : (
                            <>
                              <span className="price-amount">
                                {formatCurrency(phase.price, plan.currency)}
                              </span>
                              <span className="price-period">
                                / {t("subscription.planDetails.perMonth")}
                              </span>
                            </>
                          )}
                        </div>
                        {phase.description && (
                          <div className="phase-note">{phase.description}</div>
                        )}
                      </div>
                      {index < (plan.pricingPhases?.length ?? 0) - 1 && (
                        <div className="phase-arrow">
                          <i className="bi bi-arrow-right"></i>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Employee Pricing Info */}
            {plan.billingModel === "PER_EMPLOYEE" && (
              <div className="employee-pricing-container">
                <h5 className="section-title">
                  <i className="bi bi-people-fill"></i> Employee Pricing
                </h5>
                <div className="employee-pricing-cards">
                  {plan.includedEmployees && plan.includedEmployees > 0 && (
                    <div className="pricing-info-card included-card">
                      <div className="card-icon">
                        <i className="bi bi-gift"></i>
                      </div>
                      <div className="card-content">
                        <div className="card-label">
                          {t("subscription.planDetails.includedEmployees")}
                        </div>
                        <div className="card-value">
                          {t("subscription.planDetails.firstEmployeesFree", {
                            count: plan.includedEmployees,
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="pricing-info-card per-employee-card">
                    <div className="card-icon">
                      <i className="bi bi-person-plus"></i>
                    </div>
                    <div className="card-content">
                      <div className="card-label">
                        {t("subscription.planDetails.perAdditionalEmployee")}
                      </div>
                      <div className="card-value">
                        {formatCurrency(plan.pricePerEmployee, plan.currency)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Standard pricing (no phases) */}
            {(!plan.pricingPhases || plan.pricingPhases.length === 0) && (
              <div className="standard-pricing-container">
                <div className="pricing-info-card standard-card">
                  <div className="card-icon">
                    <i className="bi bi-cash-coin"></i>
                  </div>
                  <div className="card-content">
                    <div className="card-label">
                      {plan.billingModel === "FIXED_MONTHLY"
                        ? t("subscription.planDetails.monthlyPrice")
                        : t("subscription.planDetails.basePrice")}
                    </div>
                    <div className="card-value">
                      {formatCurrency(plan.basePrice, plan.currency)}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="stats-section">
        <div className="row">
          <div className="col-md-3">
            <div className="stat-card paid">
              <div className="stat-value">{filteredStats.totalMonthsPaid}</div>
              <div className="stat-label">
                {t("subscription.stats.monthsPaid")}
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="stat-card unpaid">
              <div className="stat-value">
                {filteredStats.totalMonthsUnpaid}
              </div>
              <div className="stat-label">
                {t("subscription.stats.monthsUnpaid")}
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="stat-card overdue">
              <div className="stat-value">
                {filteredStats.totalMonthsOverdue}
              </div>
              <div className="stat-label">
                {t("subscription.stats.monthsOverdue")}
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="stat-card revenue">
              <div className="stat-value">
                {formatCurrency(filteredStats.totalRevenue, plan.currency)}
              </div>
              <div className="stat-label">
                {t("subscription.stats.totalRevenue")}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Note */}
      <div className="subscription-footer">
        <p className="text-muted">
          <i className="bi bi-info-circle"></i> {t("subscription.footer.note")}
        </p>
      </div>
    </div>
  );
};

export default MySubscription;
